// Live Dashboard — operational hub for owner + cashier.
//
// Owner sees: active events, today's sales, pending delivery, top products,
// recent transactions, active events list, quick admin actions.
// Cashier sees: today's sales (scoped to their own tx), pending delivery
// (scoped to theirs), recent transactions (theirs), quick POS action.

import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import {
  CalendarRange,
  PackageCheck,
  ShoppingBag,
  TrendingUp,
  Clock,
  AlertCircle,
  Plus,
  ScanLine,
  Receipt,
  UserPlus,
  Wallet,
  AlertTriangle,
  ArrowUpRight,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useActiveEvents, useEventHistory } from '@/hooks/useEvents';
import { useRecentTransactions, useTransactionsByEvent } from '@/hooks/useTransactions';
import { useDashboardStats } from '@/hooks/useDashboardStats';
import { useAllOutstanding } from '@/hooks/useReceivables';
import { derivePaymentStatus } from '@/types/domain';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { Select } from '@/components/ui/Select';
import { LoadingState } from '@/components/ui/LoadingState';
import { EmptyState } from '@/components/ui/EmptyState';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { usePermissions } from '@/hooks/usePermissions';
import { formatIDR, formatRelative, formatDateTime, formatNumber, formatDate, cn } from '@/lib/format';
import type { AppTransaction, PaymentStatus } from '@/types/domain';

const STATUS_LABEL: Record<PaymentStatus, string> = {
  paid: 'Paid',
  pending_delivery: 'Pending delivery',
  pending_payment: 'Tempo',
  overdue: 'Overdue',
  partially_returned: 'Partially returned',
  returned: 'Returned',
  cancelled: 'Cancelled',
};

const STATUS_VARIANT: Record<PaymentStatus, 'success' | 'warning' | 'info' | 'danger' | 'default'> = {
  paid: 'success',
  pending_delivery: 'warning',
  pending_payment: 'info',
  overdue: 'danger',
  partially_returned: 'info',
  returned: 'default',
  cancelled: 'danger',
};

export default function Dashboard() {
  const { user } = useAuth();
  // Money and the all-cashier views need reports.view; everyone else gets
  // their own shift. (The variable name predates the permission matrix.)
  const { can } = usePermissions();
  const isUserOwner = can('reports', 'view');
  const { data: stats, isLoading: loadingStats, dataUpdatedAt } = useDashboardStats();
  const { data: events, isLoading: loadingEvents } = useActiveEvents();
  const { data: recent, isLoading: loadingRecent } = useRecentTransactions(10);

  // Event filter for the Transaksi section. Empty string = all events.
  // Default: empty (so existing behavior is preserved on first load —
  // "Transaksi terbaru" still shows the 10 most recent across events).
  const [txEventFilter, setTxEventFilter] = useState<string>('');

  // Active + history events for the filter dropdown. We need both
  // because the user might want to look at a closed event's history.
  const { data: historyEvents } = useEventHistory();

  // When a specific event is picked, fetch its full transaction list.
  // For the "all events" view we reuse the recent 50.
  const { data: eventTxList, isLoading: loadingEventTx } = useTransactionsByEvent(
    txEventFilter || null,
  );

  // Cashier view: filter to their own transactions. We still pull the
  // recent 50 so the cashier's own numbers (and pending delivery count)
  // are scoped correctly. Owner sees the global rollup.
  const myRecent: AppTransaction[] = (recent ?? []).filter(
    (t) => !user?.uid || t.cashierId === user.uid,
  );

  // Resolve the visible tx list based on the filter + role.
  // - Owner: respect the event filter (or fall back to all-recent)
  // - Cashier: only their own tx; if an event filter is set, intersect
  //   with the event's tx list
  let txList: AppTransaction[];
  if (txEventFilter) {
    // Filtered by event — restrict to the cashier's own tx if not owner
    const all = eventTxList ?? [];
    txList = isUserOwner ? all : all.filter((t) => t.cashierId === user?.uid);
  } else {
    // No filter — global recent, scoped by role
    txList = isUserOwner ? recent ?? [] : myRecent.length > 0 ? myRecent : [];
  }

  const loadingTx = txEventFilter ? loadingEventTx : loadingRecent;

  const lastUpdated = dataUpdatedAt ? dataUpdatedAt : null;

  return (
    <>
      <PageHeader
        overline={`Selamat datang, ${user?.displayName ?? ''}`}
        title="Dashboard"
        description={
          isUserOwner
            ? 'Ringkasan event aktif, penjualan hari ini, dan aksi cepat owner.'
            : 'Ringkasan shift kamu hari ini — penjualan, pending delivery, dan transaksi terakhir.'
        }
        actions={
          lastUpdated && (
            <span className="text-caption text-ink-subtle flex items-center gap-1.5">
              <Clock size={13} />
              Updated {formatRelative(lastUpdated)}
            </span>
          )
        }
      />

      {/* === Stat cards === */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <StatCard
          icon={<CalendarRange size={18} />}
          label={isUserOwner ? 'Event aktif' : 'Event berlangsung'}
          value={loadingEvents ? '…' : String(events?.length ?? 0)}
          sub={events?.[0]?.name ?? (isUserOwner ? 'Buat event baru untuk mulai' : 'Tanya owner untuk buka event')}
          tone="primary"
        />
        <StatCard
          icon={<ShoppingBag size={18} />}
          label="Transaksi hari ini"
          value={loadingStats ? '…' : String(stats?.todayTransactions ?? 0)}
          sub={
            stats && stats.todayCancelledCount > 0
              ? `${stats.todayCancelledCount} cancelled`
              : 'Semua sukses'
          }
          tone="success"
        />
        <StatCard
          icon={<TrendingUp size={18} />}
          label="Penjualan hari ini"
          value={loadingStats ? '…' : formatIDR(stats?.todaySales ?? 0)}
          sub={isUserOwner ? 'Semua kasir' : 'Shift kamu'}
          tone="primary"
        />
        <StatCard
          icon={<PackageCheck size={18} />}
          label="Pending delivery"
          value={loadingStats ? '…' : String(stats?.pendingDeliveryCount ?? 0)}
          sub={
            stats && stats.pendingDeliveryCount > 0
              ? 'Belum di-fulfill'
              : 'Semua terkirim'
          }
          tone={stats && stats.pendingDeliveryCount > 0 ? 'warning' : 'success'}
        />
      </div>

      {/* === Outstanding receivables (piutang) — owner only === */}
      {isUserOwner && (
        <div className="mb-6">
          <OutstandingReceivablesCard />
        </div>
      )}

      {/* === Recent transactions + Quick actions === */}
      <div className="grid gap-4 lg:grid-cols-3 mb-6">
        <Card padding="none" className="lg:col-span-2">
          <div className="px-5 py-4 border-b border-line flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h3 className="text-subhead font-display font-semibold text-ink">
                Transaksi {txEventFilter ? 'per event' : 'terbaru'}
              </h3>
              <p className="text-caption text-ink-subtle mt-0.5">
                {txEventFilter
                  ? `Event dipilih — ${formatNumber(txList.length)} transaksi`
                  : isUserOwner
                    ? '10 transaksi terakhir dari semua kasir'
                    : '10 transaksi terakhir kamu'}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="w-56">
                <Select
                  value={txEventFilter}
                  onChange={(value) => setTxEventFilter(value)}
                  aria-label="Filter transaksi per event"
                >
                  <option value="">Semua event</option>
                  {events && events.length > 0 && (
                    <optgroup label="Event aktif">
                      {events.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.name} ({e.code})
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {historyEvents && historyEvents.length > 0 && (
                    <optgroup label="History (closed)">
                      {historyEvents.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.name} ({e.code})
                        </option>
                      ))}
                    </optgroup>
                  )}
                </Select>
              </div>
              <Link to="/transactions" className="hidden sm:inline-block">
                <Button variant="ghost" size="sm">
                  Lihat semua
                </Button>
              </Link>
            </div>
          </div>

          {loadingTx ? (
            <LoadingState message="Memuat transaksi…" />
          ) : txList.length === 0 ? (
            <EmptyState
              icon={<Receipt size={24} strokeWidth={1.5} />}
              title={
                txEventFilter
                  ? 'Belum ada transaksi di event ini'
                  : isUserOwner
                    ? 'Belum ada transaksi'
                    : 'Belum ada transaksi hari ini'
              }
              description={
                txEventFilter
                  ? 'Coba pilih event lain di filter di atas, atau kembali ke semua event.'
                  : isUserOwner
                    ? 'Mulai buka POS atau buat event untuk menerima transaksi pertama.'
                    : 'Buka POS dan lakukan transaksi pertama shift ini.'
              }
              action={
                !txEventFilter && (
                  <Link to="/pos">
                    <Button variant="primary" leftIcon={<ScanLine size={16} />}>
                      Buka POS
                    </Button>
                  </Link>
                )
              }
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Invoice</TH>
                  <TH>Tanggal</TH>
                  <TH>Kasir</TH>
                  <TH>Payment</TH>
                  <TH>Status</TH>
                  <TH className="text-right">Total</TH>
                </TR>
              </THead>
              <TBody>
                {txList.slice(0, 10).map((t) => (
                  <TR key={t.id}>
                    <TD>
                      <Link
                        to={`/invoice/${t.id}`}
                        className="font-mono text-caption text-primary hover:underline"
                      >
                        {t.invoiceNumber}
                      </Link>
                    </TD>
                    <TD>
                      <span className="text-caption text-ink-subtle whitespace-nowrap">
                        {formatRelative(t.createdAt)}
                      </span>
                    </TD>
                    <TD>
                      <span className="text-small text-ink">
                        {t.cashierName ?? t.cashierId.slice(0, 6)}
                      </span>
                    </TD>
                    <TD>
                      <span className="chip capitalize">{t.paymentMethod}</span>
                    </TD>
                    <TD>
                      <Chip variant={STATUS_VARIANT[t.paymentStatus]}>
                        {STATUS_LABEL[t.paymentStatus]}
                      </Chip>
                    </TD>
                    <TD className="text-right">
                      <span className="font-medium text-ink whitespace-nowrap">
                        {formatIDR(t.grandTotal)}
                      </span>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </Card>

        <Card>
          <CardTitle>Aksi cepat</CardTitle>
          <CardDescription>Hal yang paling sering kamu lakukan.</CardDescription>
          <div className="mt-4 flex flex-col gap-2">
            <Link to="/pos">
              <Button variant="primary" className="w-full justify-start" leftIcon={<ScanLine size={16} />}>
                Buka POS
              </Button>
            </Link>
            {isUserOwner && (
              <>
                <Link to="/events">
                  <Button variant="secondary" className="w-full justify-start" leftIcon={<Plus size={16} />}>
                    Mulai event baru
                  </Button>
                </Link>
                <Link to="/inventory">
                  <Button variant="secondary" className="w-full justify-start" leftIcon={<Plus size={16} />}>
                    Tambah produk
                  </Button>
                </Link>
                <Link to="/customers">
                  <Button variant="secondary" className="w-full justify-start" leftIcon={<UserPlus size={16} />}>
                    Tambah customer
                  </Button>
                </Link>
                <Link to="/reports">
                  <Button variant="ghost" className="w-full justify-start" leftIcon={<TrendingUp size={16} />}>
                    Lihat reports
                  </Button>
                </Link>
              </>
            )}
            {!isUserOwner && (
              <>
                <Link to="/transactions">
                  <Button variant="secondary" className="w-full justify-start" leftIcon={<Receipt size={16} />}>
                    Riwayat transaksi
                  </Button>
                </Link>
                <Link to="/customers">
                  <Button variant="secondary" className="w-full justify-start" leftIcon={<UserPlus size={16} />}>
                    Cari customer
                  </Button>
                </Link>
              </>
            )}
          </div>
        </Card>
      </div>

      {/* === Active events (owner) === */}
      {isUserOwner && (
        <div className="grid gap-4 lg:grid-cols-2 mb-6">
          <Card>
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="text-subhead font-display font-semibold text-ink">
                  Event aktif
                </h3>
                <p className="text-caption text-ink-subtle mt-0.5">
                  Event yang sedang berjalan
                </p>
              </div>
              <Link to="/events">
                <Button variant="ghost" size="sm">
                  Kelola event
                </Button>
              </Link>
            </div>
            {loadingEvents ? (
              <LoadingState message="Memuat event…" />
            ) : !events || events.length === 0 ? (
              <EmptyState
                icon={<CalendarRange size={24} strokeWidth={1.5} />}
                title="Belum ada event aktif"
                description="Buat event baru dari menu Events untuk mulai terima penjualan."
                action={
                  <Link to="/events">
                    <Button variant="primary" leftIcon={<Plus size={16} />}>
                      Buat event
                    </Button>
                  </Link>
                }
              />
            ) : (
              <ul className="divide-y divide-line -mx-2">
                {events.map((e) => (
                  <li key={e.id} className="px-2 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-ink truncate">{e.name}</p>
                      <p className="text-caption text-ink-subtle mt-0.5">
                        <code className="font-mono">{e.code}</code>
                        {e.location ? ` · ${e.location}` : ''}
                        {' · '}
                        Dimulai {formatRelative(e.startAt)}
                      </p>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <p className="font-semibold text-ink">{formatIDR(e.totalSales)}</p>
                      <p className="text-caption text-ink-subtle">
                        {e.totalTransactions} transaksi
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="text-subhead font-display font-semibold text-ink">
                  Top produk hari ini
                </h3>
                <p className="text-caption text-ink-subtle mt-0.5">
                  5 produk dengan revenue terbesar
                </p>
              </div>
            </div>
            {loadingStats ? (
              <LoadingState message="Menghitung…" />
            ) : !stats || stats.todayTopProducts.length === 0 ? (
              <EmptyState
                icon={<AlertCircle size={24} strokeWidth={1.5} />}
                title="Belum ada penjualan hari ini"
                description="Transaksi pertama hari ini akan muncul di sini."
              />
            ) : (
              <ul className="divide-y divide-line -mx-2">
                {stats.todayTopProducts.map((p, idx) => (
                  <li key={p.productId} className="px-2 py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-7 h-7 rounded-full bg-primary/10 text-primary text-caption font-semibold flex items-center justify-center flex-shrink-0">
                        {idx + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium text-ink truncate">{p.name}</p>
                        <p className="text-caption text-ink-subtle">
                          {p.qty} terjual
                        </p>
                      </div>
                    </div>
                    <span className="font-semibold text-ink whitespace-nowrap">
                      {formatIDR(p.subtotal)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}

      <p className="text-caption text-ink-subtle text-center mt-8">
        Last refresh {dataUpdatedAt ? formatDateTime(dataUpdatedAt) : '—'}
      </p>
    </>
  );
}

// ==== Outstanding Receivables (piutang) — Phase 6 ====

/**
 * Owner-only card on the dashboard. Shows:
 *   - Total outstanding amount (sum of outstandingAmount across all txns)
 *   - Overdue count (derived: dueDate < now && outstanding > 0)
 *   - Top 5 debtors (customers with the largest outstanding, with their
 *     most-overdue invoice for context)
 */
function OutstandingReceivablesCard() {
  const { data: outstanding, isLoading } = useAllOutstanding();

  const totals = useMemo(() => {
    if (!outstanding) {
      return { total: 0, overdueCount: 0, customerMap: new Map<string, { name: string; total: number; overdueCount: number; soonestDue: number | null }>() };
    }
    let total = 0;
    let overdueCount = 0;
    const customerMap = new Map<string, { name: string; total: number; overdueCount: number; soonestDue: number | null }>();
    for (const tx of outstanding) {
      total += tx.outstandingAmount;
      const isOverdue = derivePaymentStatus(tx) === 'overdue';
      if (isOverdue) overdueCount += 1;
      const custId = tx.customerId ?? 'walkin';
      const name = tx.customerSnapshot?.name ?? 'Tanpa nama';
      const existing = customerMap.get(custId);
      const due = tx.dueDate ?? null;
      if (!existing) {
        customerMap.set(custId, {
          name,
          total: tx.outstandingAmount,
          overdueCount: isOverdue ? 1 : 0,
          soonestDue: due,
        });
      } else {
        existing.total += tx.outstandingAmount;
        if (isOverdue) existing.overdueCount += 1;
        if (due !== null && (existing.soonestDue === null || due < existing.soonestDue)) {
          existing.soonestDue = due;
        }
      }
    }
    return { total, overdueCount, customerMap };
  }, [outstanding]);

  const topDebtors = useMemo(() => {
    return Array.from(totals.customerMap.entries())
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  }, [totals.customerMap]);

  return (
    <Card padding="none">
      <div className="px-5 py-4 border-b border-line flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-warning/10 text-warning flex items-center justify-center flex-shrink-0">
            <Wallet size={16} />
          </span>
          <div>
            <CardTitle>Piutang Outstanding</CardTitle>
            <CardDescription>
              Tagihan yang belum lunas — semua customer & semua event.
            </CardDescription>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-overline uppercase text-ink-subtle font-medium">
              Total piutang
            </p>
            <p className="text-subhead font-display font-semibold text-ink tabular-nums">
              {isLoading ? '…' : formatIDR(totals.total)}
            </p>
          </div>
          {totals.overdueCount > 0 && (
            <Chip variant="danger">
              <AlertTriangle size={11} className="mr-0.5" />
              {totals.overdueCount} overdue
            </Chip>
          )}
        </div>
      </div>

      {isLoading ? (
        <LoadingState message="Memuat piutang…" />
      ) : !outstanding || outstanding.length === 0 ? (
        <div className="px-5 py-8 text-center text-caption text-ink-subtle">
          Tidak ada piutang aktif. Semua transaksi sudah lunas 🎉
        </div>
      ) : (
        <div className="divide-y divide-line">
          {topDebtors.map((d) => {
            const dueDate = d.soonestDue;
            const isOverdue = dueDate !== null && dueDate < Date.now();
            return (
              <Link
                key={d.id}
                to={`/customers/${d.id === 'walkin' ? '' : d.id}`}
                className="block px-5 py-3 hover:bg-canvas transition-colors"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-small font-medium text-ink truncate">
                      {d.name}
                    </p>
                    <p className="text-caption text-ink-subtle mt-0.5 flex items-center gap-2 flex-wrap">
                      {d.overdueCount > 0 && (
                        <span className="text-danger font-medium">
                          {d.overdueCount} overdue
                        </span>
                      )}
                      {dueDate && (
                        <span
                          className={cn(
                            'flex items-center gap-1',
                            isOverdue ? 'text-danger' : 'text-ink-subtle',
                          )}
                        >
                          · Jatuh tempo {formatDate(dueDate)}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="text-right flex items-center gap-2 flex-shrink-0">
                    <span
                      className={cn(
                        'font-semibold tabular-nums text-body',
                        d.overdueCount > 0 ? 'text-danger' : 'text-ink',
                      )}
                    >
                      {formatIDR(d.total)}
                    </span>
                    <ArrowUpRight size={14} className="text-ink-subtle" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </Card>
  );
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  tone: 'primary' | 'success' | 'warning';
}

function StatCard({ icon, label, value, sub, tone }: StatCardProps) {
  const toneClass = {
    primary: 'bg-primary/10 text-primary',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
  }[tone];
  return (
    <Card padding="md" hoverable>
      <div className="flex items-start gap-3">
        <span className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${toneClass}`}>
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-overline uppercase text-ink-subtle font-medium tracking-wider">
            {label}
          </p>
          <p className="text-metric font-display font-semibold mt-1 text-ink truncate">
            {value}
          </p>
          <p className="text-caption text-ink-subtle mt-1 truncate">{sub}</p>
        </div>
      </div>
    </Card>
  );
}
